import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useSaidaSuave } from '../hooks/useSaidaSuave';
import { fotoQuadradaBase64 } from '../utils/imagemUtils';

// Redimensiona pra um quadrado pequeno antes de virar base64 — um
// avatar não precisa da resolução original da câmera do celular,
// e sem isso a coluna foto do usuário podia ficar enorme.
// (30/07/2026: extraída pra utils/imagemUtils.js — reaproveitada
// também no cadastro de produto, ver fotoQuadradaBase64.)

const iniciais = (nome) =>
  (nome || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('');


/**
 * MeuPerfilModal — foto + trocar senha (07/07/2026).
 *
 * Extraído do Sidebar.jsx pra ser compartilhado: antes só existia lá
 * dentro, e como a navegação é Sidebar OU Dock (nunca os dois montados
 * juntos), quem usava Dock não tinha NENHUM jeito de chegar nesse
 * modal — o chip de usuário do topbar em modo Dock já existia
 * visualmente, mas sem onClick nenhum. Agora os dois pontos de entrada
 * (avatar do Sidebar, chip do topbar em modo Dock) abrem esta mesma
 * instância.
 */
export default function MeuPerfilModal({ aberto, onClose }) {
  const { montado, fechando } = useSaidaSuave(aberto, 200, onClose);
  const { usuario, fotoUrl, alterarFoto, API } = useAuth();
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [erroFoto, setErroFoto] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [senhaNova2, setSenhaNova2] = useState('');
  const [msgSenha, setMsgSenha] = useState(null); // { tipo:'ok'|'erro', texto }
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  /* ⚠ UM ESTADO POR CAMPO, e não um só para os três (08/09/2026,
     apontado pelo dono do produto: "a senha atual não aparece para
     visualizar no celular").

     Duas coisas estavam erradas. O olho existia SÓ no campo "Nova
     senha" — os outros dois não tinham como revelar. E o estado era
     compartilhado: clicar naquele único olho descobria os TRÊS ao mesmo
     tempo, o que num celular na fábrica, com gente ao redor, mostra a
     senha atual de quem só queria conferir se digitou a nova certo.

     Revelar é decisão por campo. */
  const [verAtual, setVerAtual] = useState(false);
  const [verNova, setVerNova] = useState(false);
  const [verNova2, setVerNova2] = useState(false);
  if (!montado) return null;

  const trocarSenha = async () => {
    setMsgSenha(null);
    if (!senhaAtual || !senhaNova || !senhaNova2) {
      setMsgSenha({ tipo: 'erro', texto: 'Preencha os três campos.' }); return;
    }
    if (senhaNova !== senhaNova2) {
      setMsgSenha({ tipo: 'erro', texto: 'A nova senha não confere nos dois campos.' }); return;
    }
    if (senhaNova.length < 6) {
      setMsgSenha({ tipo: 'erro', texto: 'A nova senha deve ter pelo menos 6 caracteres.' }); return;
    }
    setSalvandoSenha(true);
    try {
      await API.put('/auth/senha', { senhaAtual, novaSenha: senhaNova });
      setSenhaAtual(''); setSenhaNova(''); setSenhaNova2('');
      setMsgSenha({ tipo: 'ok', texto: 'Senha alterada com sucesso.' });
    } catch (err) {
      setMsgSenha({ tipo: 'erro', texto: err.response?.data?.erro || 'Erro ao trocar a senha.' });
    } finally { setSalvandoSenha(false); }
  };

  const escolherFoto = async (file) => {
    if (!file) return;
    setErroFoto(''); setEnviandoFoto(true);
    try {
      const base64 = await fotoQuadradaBase64(file);
      await alterarFoto(base64);
    } catch {
      setErroFoto('Não deu pra processar essa imagem.');
    } finally {
      setEnviandoFoto(false);
    }
  };

  const removerFoto = async () => {
    setEnviandoFoto(true);
    try { await alterarFoto(null); }
    finally { setEnviandoFoto(false); }
  };

  return (
    <div className={`modal-overlay ${fechando ? 'closing' : ''}`} onClick={onClose}>
      <div className={`modal-rola modal ${fechando ? 'closing' : ''}`} style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ marginBottom: 4 }}>
          <h3>Meu Perfil</h3>
          <button className="acao-fechar" onClick={onClose}>×</button>
        </div>

        {/* Avatar — clicável, com overlay de câmera ao passar o mouse */}
        <div className="modal-corpo"><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '18px 0 6px' }}>
          <label
            className="avatar-upload-label"
            style={{
              position: 'relative', width: 96, height: 96, borderRadius: '50%', cursor: enviandoFoto ? 'default' : 'pointer',
              padding: 2, background: 'linear-gradient(135deg, var(--ext), var(--sem-info-text))' }}
          >
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden',
              background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--txt-30)', fontWeight: 500, color: 'var(--text-secondary)', fontFamily: 'var(--display)' }}>
              {fotoUrl
                ? <img src={fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (iniciais(usuario?.nome) || 'U')}
            </div>
            <div className="avatar-overlay" style={{
              position: 'absolute', inset: 2, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity .15s' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.09">
                <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                <circle cx="12" cy="14" r="3.5"/>
              </svg>
            </div>
            <input type="file" accept="image/*" style={{ display: 'none' }} disabled={enviandoFoto}
              onChange={e => escolherFoto(e.target.files[0])} />
          </label>

          {enviandoFoto && <span style={{ fontSize: 'var(--txt-11)', fontWeight: 500, color: 'var(--text-secondary)', marginTop: 8 }}>Enviando...</span>}
          {!enviandoFoto && (
            <div style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: 'var(--txt-13)', fontWeight: 500 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Clique na foto para trocar</span>
              {usuario?.foto && (
                <button onClick={removerFoto} style={{ background: 'none', border: 'none', color: 'var(--sem-danger-text)', cursor: 'pointer', fontSize: 'var(--txt-13)', fontWeight: 500, padding: 0, textDecoration: 'underline' }}>
                  Remover
                </button>
              )}
            </div>
          )}
        </div>
        {erroFoto && <div className="info-box-danger" style={{ marginTop: 10 }}>{erroFoto}</div>}

        {/* ⚠ A TROCA DE LOGIN SAIU DAQUI (08/09/2026, decisão do dono do
            produto: "o único que pode alterar login é pelo painel
            administrativo. por aqui não").

            Eu havia acrescentado o campo em V1291 e ele nunca funcionou —
            o modal quebrava ao abrir por hook depois de saída antecipada
            (React #310), corrigido só em V1307. Então a função foi
            escrita, quebrada e removida sem nunca ter rodado.

            O que fica: trocar login é ato de ADMINISTRAÇÃO, não de
            autoatendimento. O `adminController.atualizarUsuario` já faz
            isso desde 05/08/2026, com a permissão certa e o registro em
            auditoria. Duas portas para a mesma mudança de identidade era
            uma porta a mais.

            Saíram junto o `alterarLogin` do AuthContext e a rota
            `PUT /auth/login` no backend — ver as notas lá. */}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 22, paddingTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5.25" y="10.5" width="13.5" height="9.75" rx="1.5"/><path d="M 8.25 10.5 V 7.2 a 3.75 3.75 0 0 1 7.5 0 V 10.5"/>
            </svg>
            <span style={{ fontSize: 'var(--txt-11)', fontWeight: 500, letterSpacing: .4, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
              Segurança
            </span>
          </div>

          {msgSenha && (
            <div className={msgSenha.tipo === 'ok' ? 'info-box-success' : 'info-box-danger'} style={{ marginBottom: 12, fontSize: 'var(--txt-13)', fontWeight: 500 }}>
              {msgSenha.texto}
            </div>
          )}
          <div className="input-group" style={{ marginBottom: 10 }}>
            <label>Senha atual</label>
            <div className="input-senha-wrapper">
              <input className="input" type={verAtual ? 'text' : 'password'} value={senhaAtual}
                onChange={e => setSenhaAtual(e.target.value)} autoComplete="current-password" />
              <button type="button" className="btn-mostrar-senha" tabIndex={-1}
                onClick={() => setVerAtual(v => !v)}
                aria-label={verAtual ? 'Ocultar a senha' : 'Mostrar a senha'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.71" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M 3 12 c 2.25 -4.5 6 -6.75 9 -6.75 s 6.75 2.25 9 6.75 c -2.25 4.5 -6 6.75 -9 6.75 S 5.25 16.5 3 12 z" />
                  <circle cx="12" cy="12" r="3" />
                  {verAtual && <line x1="3" y1="3" x2="21" y2="21" />}
                </svg>
              </button>
            </div>
          </div>
          <div className="input-group" style={{ marginBottom: 10 }}>
            <label>Nova senha</label>
            <div className="input-senha-wrapper">
              <input className="input" type={verNova ? 'text' : 'password'} value={senhaNova}
                onChange={e => setSenhaNova(e.target.value)} autoComplete="new-password" />
              <button type="button" className="btn-mostrar-senha" tabIndex={-1}
                onClick={() => setVerNova(v => !v)}
                aria-label={verNova ? 'Ocultar a senha' : 'Mostrar a senha'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.71" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M 3 12 c 2.25 -4.5 6 -6.75 9 -6.75 s 6.75 2.25 9 6.75 c -2.25 4.5 -6 6.75 -9 6.75 S 5.25 16.5 3 12 z" />
                  <circle cx="12" cy="12" r="3" />
                  {verNova && <line x1="3" y1="3" x2="21" y2="21" />}
                </svg>
              </button>
            </div>
            <small className="input-hint">Pelo menos 6 caracteres.</small>
          </div>
          <div className="input-group" style={{ marginBottom: 18 }}>
            <label>Repetir nova senha</label>
            <div className="input-senha-wrapper">
              <input className="input" type={verNova2 ? 'text' : 'password'} value={senhaNova2}
                onChange={e => setSenhaNova2(e.target.value)} autoComplete="new-password" />
              <button type="button" className="btn-mostrar-senha" tabIndex={-1}
                onClick={() => setVerNova2(v => !v)}
                aria-label={verNova2 ? 'Ocultar a senha' : 'Mostrar a senha'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.71" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M 3 12 c 2.25 -4.5 6 -6.75 9 -6.75 s 6.75 2.25 9 6.75 c -2.25 4.5 -6 6.75 -9 6.75 S 5.25 16.5 3 12 z" />
                  <circle cx="12" cy="12" r="3" />
                  {verNova2 && <line x1="3" y1="3" x2="21" y2="21" />}
                </svg>
              </button>
            </div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={salvandoSenha} onClick={trocarSenha}>
            {salvandoSenha ? 'Salvando...' : 'Alterar senha'}
          </button>
        </div></div>
      </div>
    </div>
  );
}
